/* eslint-disable react/function-component-definition */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import Tab from '@mui/material/Tab';
import { useEffect, useState } from 'react';
import ParentScraperComponent from '../Augment/scraper/ScraperParent';
import BatchedWebEnrichment from '../Augment/scraper/web/BatchedWebEnrichment';

const LeadsSideSheetTabs = ({ panelWidth }: { panelWidth: string }) => {
  const [scraperState, setScraperState] = useState<
    'closed' | 'working' | 'open' | 'saved'
  >('closed');
  const [sessions, setSessions] = useState<any>(null);
  const [value, setValue] = useState('1');
  const [zoho, setZoho] = useState<any>(null);
  const [leadsLoading, setLeadsLoading] = useState<boolean>(false);

  const setStoreState = useAlchemystStoreForAi((store) => store.setStoreState);
  const leadSets = useAlchemystStoreForAi((store) => store.leadSets);
  const [infoOpen, setInfoOpen] = useState(false);

  const handleInfoOpen = () => setInfoOpen(true);
  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const currentTheme = useAlchemystStoreForAi((store) => store.theme);

  const fetchConnectedAccounts = async () => {
    try {
      const res = await fetchWithRewrites(
        '/api/v1/persona/maya/integrations/zoho/fetch'
      );
      const data = await res.json();

      setZoho(
        data.data.map(
          (item: {
            userId: string;
            credentials: {
              clientId: string;
            };
          }) => ({
            userId: item.userId,
            clientId: item.credentials.clientId,
          })
        )
      );
    } catch (error) {
      console.error('Error fetching connected accounts:', error);
    }
  };

  const handleFetchZohoLeads = async () => {
    try {
      setLeadsLoading(true);

      const userId = zoho?.[0]?.userId;
      if (!userId) {
        console.error('User ID is missing');
        return;
      }

      const response = await fetchWithRewrites(
        '/api/v1/persona/maya/integrations/zoho/leads',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            per_page: 100,
          }),
        }
      );

      const contentType = response.headers.get('content-type');
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got: ${text.slice(0, 100)}...`);
      }

      const result = await response.json();
      const zohoLeads = result?.leads?.data || [];

      const parsedFields =
        zohoLeads.length > 0 ? Object.keys(zohoLeads[0]) : [];

      const safeLeads = zohoLeads.map((lead: any) => {
        const safeLead: Record<string, any> = {};
        Object.entries(lead).forEach(([key, value]) => {
          if (key === 'id') return;

          if (typeof value === 'object' && value !== null) {
            const valueObj = value as Record<string, any>;

            if ('id' in valueObj) {
              const { id, ...objectWithoutId } = valueObj;
              if (id !== undefined) {
                safeLead[`${key}_id`] = id;
              }
              safeLead[key] = JSON.stringify(objectWithoutId);
            } else {
              safeLead[key] = JSON.stringify(valueObj);
            }
          } else if (value === null || value === undefined) {
            safeLead[key] = '';
          } else {
            safeLead[key] = value;
          }
        });
        return safeLead;
      });
      const newLeadSetId = `lead-set-${Date.now()}`;

      setStoreState({
        leadSets: {
          ...leadSets,
          [newLeadSetId]: {
            leads: safeLeads,
            fields: parsedFields,
            selectedLeads: [],
            fileName: 'Zoho Leads',
            dateAdded: new Date(),
          },
        },
        activeLeadSetId: newLeadSetId,
        leads: safeLeads,
        fields: parsedFields,
        selectedLeads: [],
      });

      console.log('Zoho leads loaded into store:', safeLeads);
    } catch (error) {
      console.error('Failed to fetch Zoho leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchZohoIntegrations() {
      await fetchConnectedAccounts();
    }

    fetchZohoIntegrations();
  }, []);

  return (
    <Box
      sx={{
        width: panelWidth,
        typography: 'caption',
        overflowX: 'hidden',
        border: '3px solid',
        borderRadius: 2,
        background:
          currentTheme === 'dark'
            ? 'linear-gradient(320deg, #000000 0%, #0A0A0A 100%)'
            : 'linear-gradient(320deg, #ff9a3520 1.52%, #00000000 90.82%)',
        borderImageSource:
          'linear-gradient(270deg, #00000000 1.52%, #5A5A5A20 90.98%)',
        borderImageSlice: 1,
      }}
      paddingY={'10vh'}
      height={'100vh'}
      paddingX={1}
    >
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box
            justifyContent={'center'}
            alignItems={'center'}
            textAlign={'center'}
            flex="1"
            pb={2}
            flexDirection={'row'}
          >
            <Typography variant="h5">Enrich Data</Typography>
          </Box>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            <Tab label="By URL" value="1" />
            <Tab label="By Web" value="2" />
            <Tab label="With Integrations" value="3" />
            {/* <Tab label="Sales Campaign" value="3" /> */}
          </TabList>
        </Box>
        <TabPanel value="1">
          <ParentScraperComponent mode="individual" />
        </TabPanel>
        <TabPanel value="2">
          <BatchedWebEnrichment />
        </TabPanel>
        <TabPanel value="3">
          {/* {Array.isArray(zoho) && zoho.length > 0 ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h6">Zoho Integrations Found</Typography>

              {leadsLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography>Fetching leads from Zoho...</Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleFetchZohoLeads}
                  disabled={Array.isArray(zoho) && zoho.length == 0}
                >
                  Fetch from Zoho
                </Button>
              )}
            </Box>
          ) : (
            <ComingSoon message="Why not bring more integrations here?" />
          )} */}
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">Fetch leads from Zoho</Typography>

            {leadsLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography>Fetching leads from Zoho...</Typography>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleFetchZohoLeads}
                  disabled={Array.isArray(zoho) && zoho.length === 0}
                >
                  Fetch from Zoho
                </Button>
                <Tooltip title="How to fetch leads from Zoho?">
                  <IconButton size="small" onClick={handleInfoOpen}>
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Dialog open={infoOpen} onClose={handleInfoClose}>
              <DialogTitle>Zoho Integration Guide</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  To fetch leads from Zoho, make sure your Zoho account is
                  connected under Integrations.
                  <br />
                  <br />
                  Once connected, click the "Fetch from Zoho" button to retrieve
                  up to 100 leads using the authorized user's Zoho organization.
                  <br />
                  <br />
                  If you haven't connected Zoho yet, go to{' '}
                  <strong>Integrations &gt; Zoho CRM</strong> and authorize
                  access.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleInfoClose}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default LeadsSideSheetTabs;
